// Modern UI helpers for Planyo search selects
// - mutual exclusion between Activity and Facility
// - clear (✕) buttons shown when a value is selected
// - accessible aria-live announcements when disabled controls are interacted with

export default function setupPlanyoFormUI() {
    if (typeof window === 'undefined') return;
    if (window.__planyoFormUiInit) return; // idempotent
    window.__planyoFormUiInit = true;

    const qs = (sel, ctx = document) => ctx.querySelector(sel);
    const qsa = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

    const activity = qs('#box_prop_res_activity');
    const facility = qs('#box_prop_res_rental_facility');

    // create or return an aria-live polite region
    const ensureLiveRegion = () => {
        const id = 'planyo-disabled-live-region';
        let el = document.getElementById(id);
        if (el) return el;
        el = document.createElement('div');
        el.id = id;
        el.setAttribute('aria-live', 'polite');
        el.setAttribute('role', 'status');
        Object.assign(el.style, {
            position: 'absolute',
            left: '-9999px',
            width: '1px',
            height: '1px',
            overflow: 'hidden'
        });
        document.body.appendChild(el);
        return el;
    };

    const announce = (msg) => {
        try {
            const live = ensureLiveRegion();
            live.textContent = '';
            // slight delay to ensure SR picks up change
            setTimeout(() => { live.textContent = msg; }, 50);
            setTimeout(() => { if (live.textContent === msg) live.textContent = ''; }, 3500);
        } catch (err) {
            // swallow
            console.error('planyo announce error', err);
        }
    };

    const ensureClearButton = (selectEl) => {
        if (!selectEl) return null;
        const wrapper = selectEl.closest('.homepage-hero__planyo-select-wrapper');
        if (!wrapper) return null;
        let btn = wrapper.querySelector('.homepage-hero__planyo-select-clear-target');
        if (!btn) {
            btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'homepage-hero__planyo-select-clear-target';
            btn.setAttribute('aria-label', 'Clear selection');
            btn.innerHTML = '<span class="homepage-hero__planyo-select-clear-icon">✕</span>';
            btn.style.display = 'none';
            wrapper.appendChild(btn);
        }
        return btn;
    };

    const setDisabledState = (selectEl, wrapper, disabled, title) => {
        if (!selectEl) return;
        if (disabled) {
            selectEl.setAttribute('disabled', 'disabled');
            selectEl.setAttribute('aria-disabled', 'true');
            if (wrapper) { wrapper.classList.add('is-disabled'); wrapper.setAttribute('title', title); }
        } else {
            selectEl.removeAttribute('disabled');
            selectEl.removeAttribute('aria-disabled');
            if (wrapper) { wrapper.classList.remove('is-disabled'); wrapper.removeAttribute('title'); }
        }
    };

    const updateUI = () => {
        try {
            const aVal = activity ? activity.value : 'none';
            const fVal = facility ? facility.value : 'none';
            const aWrap = activity ? activity.closest('.homepage-hero__planyo-select-wrapper') : null;
            const fWrap = facility ? facility.closest('.homepage-hero__planyo-select-wrapper') : null;

            if (activity && facility) {
                if (aVal && aVal !== 'none') {
                    setDisabledState(facility, fWrap, true, 'Please clear the Activity selection first');
                } else {
                    setDisabledState(facility, fWrap, false);
                }

                if (fVal && fVal !== 'none') {
                    setDisabledState(activity, aWrap, true, 'Please clear the Facility/Service selection first');
                } else {
                    setDisabledState(activity, aWrap, false);
                }

                const aBtn = ensureClearButton(activity);
                const fBtn = ensureClearButton(facility);
                if (aBtn) aBtn.style.display = (aVal && aVal !== 'none') ? '' : 'none';
                if (fBtn) fBtn.style.display = (fVal && fVal !== 'none') ? '' : 'none';
            }
        } catch (err) {
            console.error('planyo updateUI', err);
        }
    };

    // idempotent bind helper
    const bindOnce = (el, evt, fn) => {
        if (!el) return;
        const key = `__planyo_bound_${evt}`;
        if (el[key]) return;
        el.addEventListener(evt, fn);
        el[key] = true;
    };

    // wire clear buttons
    const aBtn = ensureClearButton(activity);
    const fBtn = ensureClearButton(facility);

    if (aBtn) bindOnce(aBtn, 'click', (e) => {
        e.preventDefault();
        if (!activity) return;
        activity.value = 'none';
        activity.removeAttribute('disabled');
        activity.removeAttribute('aria-disabled');
        if (facility) facility.removeAttribute('disabled');
        updateUI();
        announce('Activity cleared');
    });

    if (fBtn) bindOnce(fBtn, 'click', (e) => {
        e.preventDefault();
        if (!facility) return;
        facility.value = 'none';
        facility.removeAttribute('disabled');
        facility.removeAttribute('aria-disabled');
        if (activity) activity.removeAttribute('disabled');
        updateUI();
        announce('Facility/Service cleared');
    });

    // show accessible message if user interacts with disabled wrapper
    const setupDisabledInteraction = (selectEl, message) => {
        if (!selectEl) return;
        const wrapper = selectEl.closest('.homepage-hero__planyo-select-wrapper');
        if (!wrapper) return;
        if (wrapper.__planyo_disabled_handler_bound) return;
        wrapper.__planyo_disabled_handler_bound = true;

        bindOnce(wrapper, 'click', (e) => {
            if (selectEl.disabled) {
                e.preventDefault();
                e.stopPropagation();
                announce(message);
                wrapper.classList.add('planyo--flash');
                setTimeout(() => wrapper.classList.remove('planyo--flash'), 300);
            }
        });

        bindOnce(wrapper, 'keydown', (e) => {
            if (selectEl.disabled && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                e.stopPropagation();
                announce(message);
            }
        });
    };

    setupDisabledInteraction(activity, 'This field is disabled because a Facility/Service is selected. Clear the other selection to enable.');
    setupDisabledInteraction(facility, 'This field is disabled because an Activity is selected. Clear the other selection to enable.');

    if (activity) { bindOnce(activity, 'change', updateUI); bindOnce(activity, 'input', updateUI); }
    if (facility) { bindOnce(facility, 'change', updateUI); bindOnce(facility, 'input', updateUI); }

    // initial sync
    updateUI();
    // Until the page has finished loading, run a loop every 100ms to update the UI in case Planyo JS changes values
    let loopCount = 0;
    const intervalId = setInterval(() => {
        if (loopCount++ > 30) {
            clearInterval(intervalId);
            return;
        }
        updateUI();
    }, 500);
}
